import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling");
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const maxAttempts = 10;
    let timer;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {
        // continue polling
      }
      if (attemptsRef.current >= maxAttempts) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };

    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <main
      data-testid="billing-success-page"
      className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col items-center justify-center px-6 py-16 text-center"
    >
      {status === "polling" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-blue-700" />
          <h1 className="mt-6 font-heading text-2xl font-semibold">
            Confirming your payment…
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Hang tight, this only takes a moment.
          </p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="mt-6 font-heading text-3xl font-semibold">
            You're subscribed.
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Welcome to Hireloop PRO. Let's get hiring.
          </p>
          <Link
            to="/dashboard"
            data-testid="billing-success-dashboard-link"
            className="bg-brand-gradient mt-8 inline-block px-5 py-3 text-sm font-medium text-white"
          >
            Go to dashboard →
          </Link>
        </>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="mt-6 font-heading text-2xl font-semibold">
            Payment not confirmed
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            We couldn't confirm your payment yet. If you completed checkout, give
            it a minute and refresh your billing page.
          </p>
          <Link
            to="/billing"
            className="mt-6 inline-block border border-slate-300 px-5 py-3 text-sm hover:bg-slate-50"
          >
            Back to billing
          </Link>
        </>
      )}
    </main>
  );
}
